import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getListStudentsQueryKey,
  getGetEventAttendanceQueryKey,
  getListRollCallsQueryKey,
  useListStudents,
  useCreateStudent,
  useCheckInStudent,
  type CheckoutType,
  type Student,
} from '@cdf/api-client-react';
import { SearchIcon, UserPlusIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const CHECKOUT_OPTIONS: { value: CheckoutType; label: string }[] = [
  { value: 'by_self', label: 'Go home independently' },
  { value: 'wait_for_parent', label: 'Wait for parent pickup' },
  { value: 'with_person', label: 'Leave with named person' },
  { value: 'with_teacher', label: 'Leave with teacher' },
  { value: 'back_to_school', label: 'Return to school' },
];

interface AddStudentDialogProps {
  eventId: string;
  existingStudentIds: Set<string>;
}

export function AddStudentDialog({ eventId, existingStudentIds }: AddStudentDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [tab, setTab] = React.useState<'existing' | 'new'>('existing');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11 shrink-0 gap-1.5"
        >
          <UserPlusIcon className="size-4" />
          Add Student
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Student to Event</DialogTitle>
          <DialogDescription>
            Check in an existing student or create a new one. They'll be added to all roll calls.
          </DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={(v) => setTab(v as 'existing' | 'new')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="existing">Existing Student</TabsTrigger>
            <TabsTrigger value="new">New Student</TabsTrigger>
          </TabsList>
          <TabsContent value="existing">
            <ExistingStudentPicker
              eventId={eventId}
              existingStudentIds={existingStudentIds}
              search={search}
              setSearch={setSearch}
              onAdded={() => setOpen(false)}
            />
          </TabsContent>
          <TabsContent value="new">
            <NewStudentForm
              eventId={eventId}
              onAdded={() => setOpen(false)}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function ExistingStudentPicker({
  eventId,
  existingStudentIds,
  search,
  setSearch,
  onAdded,
}: {
  eventId: string;
  existingStudentIds: Set<string>;
  search: string;
  setSearch: (s: string) => void;
  onAdded: () => void;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useListStudents();
  const checkInMutation = useCheckInStudent();
  const [addingId, setAddingId] = React.useState<string | null>(null);

  const allStudents = data?.students ?? [];
  const available = allStudents.filter((s) => !existingStudentIds.has(s.id));
  const filtered = search.trim()
    ? available.filter((s) => {
        const q = search.toLowerCase();
        return (
          s.firstName.toLowerCase().includes(q) ||
          s.lastInitial.toLowerCase().includes(q) ||
          (s.group ?? '').toLowerCase().includes(q)
        );
      })
    : available;

  const handleAdd = async (student: Student) => {
    setAddingId(student.id);
    try {
      await checkInMutation.mutateAsync({
        id: eventId,
        data: { studentId: student.id },
      });
      await queryClient.invalidateQueries({ queryKey: getGetEventAttendanceQueryKey(eventId) });
      await queryClient.invalidateQueries({ queryKey: getListRollCallsQueryKey(eventId) });
      onAdded();
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <SearchIcon className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
        <Input
          className="pl-9"
          placeholder="Search by name or group…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      {isLoading ? (
        <p className="text-muted-foreground py-6 text-center text-sm">Loading students…</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-sm">
          {available.length === 0
            ? 'All students are already in this event.'
            : 'No students match your search.'}
        </p>
      ) : (
        <div className="max-h-[40vh] space-y-1 overflow-y-auto">
          {filtered.map((s) => (
            <div
              key={s.id}
              className="flex items-center justify-between rounded-md border px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {s.firstName} {s.lastInitial}
                </p>
                {s.group && (
                  <p className="text-muted-foreground truncate text-xs">{s.group}</p>
                )}
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={addingId === s.id || checkInMutation.isPending}
                onClick={() => void handleAdd(s)}
              >
                {addingId === s.id ? 'Adding…' : 'Add'}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewStudentForm({ eventId, onAdded }: { eventId: string; onAdded: () => void }) {
  const queryClient = useQueryClient();
  const createMutation = useCreateStudent();
  const checkInMutation = useCheckInStudent();
  const [form, setForm] = React.useState({
    firstName: '',
    lastInitial: '',
    group: '',
    guardianName: '',
    guardianPhone: '',
    defaultCheckoutType: '__none__',
    defaultAuthorizedPersonName: '',
  });
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const created = await createMutation.mutateAsync({
        data: {
          firstName: form.firstName,
          lastInitial: form.lastInitial,
          group: form.group,
          guardianName: form.guardianName || undefined,
          guardianPhone: form.guardianPhone || undefined,
          defaultCheckoutType:
            form.defaultCheckoutType === '__none__'
              ? null
              : (form.defaultCheckoutType as CheckoutType),
          defaultAuthorizedPersonName:
            form.defaultCheckoutType === 'with_person'
              ? form.defaultAuthorizedPersonName || null
              : null,
        },
      });
      await checkInMutation.mutateAsync({
        id: eventId,
        data: { studentId: created.id },
      });
      await queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
      await queryClient.invalidateQueries({ queryKey: getGetEventAttendanceQueryKey(eventId) });
      await queryClient.invalidateQueries({ queryKey: getListRollCallsQueryKey(eventId) });
      setForm({
        firstName: '',
        lastInitial: '',
        group: '',
        guardianName: '',
        guardianPhone: '',
        defaultCheckoutType: '__none__',
        defaultAuthorizedPersonName: '',
      });
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create student');
    }
  };

  const saving = createMutation.isPending || checkInMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <p className="text-destructive text-sm">{error}</p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="as-first">First Name</Label>
          <Input
            id="as-first"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            required
            autoFocus
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="as-last">Last Initial</Label>
          <Input
            id="as-last"
            maxLength={3}
            placeholder="S"
            value={form.lastInitial}
            onChange={(e) => setForm({ ...form, lastInitial: e.target.value })}
            required
          />
        </div>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="as-group">Group</Label>
        <Input
          id="as-group"
          placeholder="Group A"
          value={form.group}
          onChange={(e) => setForm({ ...form, group: e.target.value })}
          required
        />
      </div>
      <div className="border-t pt-4">
        <p className="text-muted-foreground mb-3 text-sm font-medium">Default departure approval</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="as-checkout">Default mode</Label>
            <Select
              value={form.defaultCheckoutType}
              onValueChange={(v) => setForm({ ...form, defaultCheckoutType: v })}
            >
              <SelectTrigger id="as-checkout">
                <SelectValue placeholder="No default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {CHECKOUT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.defaultCheckoutType === 'with_person' && (
            <div className="grid gap-2">
              <Label htmlFor="as-person">Authorized person name</Label>
              <Input
                id="as-person"
                value={form.defaultAuthorizedPersonName}
                onChange={(e) =>
                  setForm({ ...form, defaultAuthorizedPersonName: e.target.value })
                }
                placeholder="e.g. Grandma Sue"
              />
            </div>
          )}
        </div>
      </div>
      <div className="border-t pt-4">
        <p className="text-muted-foreground mb-3 text-sm font-medium">
          Guardian (for SMS matching)
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="as-gname">Guardian Name</Label>
            <Input
              id="as-gname"
              value={form.guardianName}
              onChange={(e) => setForm({ ...form, guardianName: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="as-gphone">Guardian Phone</Label>
            <Input
              id="as-gphone"
              placeholder="+61412345678"
              value={form.guardianPhone}
              onChange={(e) => setForm({ ...form, guardianPhone: e.target.value })}
            />
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Create & Add to Event'}
        </Button>
      </DialogFooter>
    </form>
  );
}
